import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BarChart3, Users, LogOut, Menu } from 'lucide-react';
// S16 (2026-06-04) — Settings icon dropped: the top-bar Settings
// button was a duplicate of the sidebar Settings entry. The two
// remaining nav buttons (Dashboard, Community) now actually
// navigate (pre-S16 all three were dead — no onClick, no href).
import { useAuth } from '../../contexts/AuthContext';
import AuthModal from '../Auth/AuthModal';
import ActiveStrategyBadge from './ActiveStrategyBadge';

// Feedback widget lives in the header now (top-right next to Sign Out).
// Lazy because it pulls in a Supabase client + modal; users who never
// click it shouldn't pay that cost on first paint.
const FeedbackWidget = lazy(() => import('../Feedback/FeedbackWidget'));

interface HeaderProps {
  // M-F — mobile sidebar drawer toggle. App.tsx owns the open state and
  // hides the desktop sidebar via Tailwind responsive classes; this
  // hamburger button is only visible at <md breakpoint.
  onMobileMenuToggle?: () => void;
}

/** S16 (2026-06-04) — Navigation handler. Pre-S16 the nav buttons
 *  had no onClick. App.tsx uses hash-based routing (#builder,
 *  #optimize, …); setting window.location.hash is what the
 *  popstate/hashchange listeners over there pick up to flip
 *  activeTab. We use replaceState (not pushState) so a stale
 *  pre-app-shell hash doesn't pollute Back history. */
function navigateToTab(tab: string) {
  const target = '#' + tab;
  if (window.location.hash !== target) {
    window.location.hash = target;
  }
}

const Header: React.FC<HeaderProps> = ({ onMobileMenuToggle }) => {
  // S26 (2026-06-04) — `isAdmin` from AuthContext gates the Dashboard
  // button visibility. Non-admins never see it (the admin route
  // remains reachable via the URL hash, but AdminPage's existing
  // access-gate handles that as defense-in-depth).
  const { user, isAdmin, signOut, inPasswordRecovery } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');

  // When AuthContext flags a recovery flow (user just clicked a reset-email
  // link), force the modal open so they can set their new password.
  useEffect(() => {
    if (inPasswordRecovery) setShowAuthModal(true);
  }, [inPasswordRecovery]);

  const handleSignIn = () => {
    setAuthMode('signin');
    setShowAuthModal(true);
  };

  const handleSignUp = () => {
    setAuthMode('signup');
    setShowAuthModal(true);
  };

  return (
    <>
      <header className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white shadow-xl">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3 md:space-x-4">
              {/* M-F — mobile hamburger. Only rendered for signed-in users
                  (the landing page has its own nav) and only visible below
                  the md breakpoint via Tailwind classes. */}
              {onMobileMenuToggle && (
                <button
                  onClick={onMobileMenuToggle}
                  className="md:hidden p-2 -ml-2 text-slate-200 hover:text-white hover:bg-white/10 rounded-md"
                  aria-label="Open navigation menu"
                >
                  <Menu className="w-5 h-5" />
                </button>
              )}
              <div className="relative">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-tr from-indigo-500 via-blue-500 to-cyan-500 rounded-lg shadow-lg" />
                <div className="absolute inset-0 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center">
                  <svg
                    viewBox="0 0 24 24"
                    className="w-6 h-6 md:w-7 md:h-7 text-white"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M2 2v20h20" />
                    <path d="M6 16l6-8 4 4 6-8" />
                  </svg>
                </div>
              </div>
              <div>
                <div className="flex items-center">
                  <span className="text-xl font-bold text-white">Tradeatory</span>
                </div>
                <div className="flex items-center mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 mr-2" />
                  <span className="text-xs font-medium text-slate-300">Strategy + Alerts</span>
                </div>
              </div>
            </div>

            {/* S16 (2026-06-04) — Dashboard + Community now navigate
                (pre-S16 both were dead — no onClick). Settings button
                removed: it was a duplicate of the sidebar Settings
                entry. Only rendered to signed-in users (matches the
                rest of the in-app nav).
                S26 (2026-06-04) — Dashboard button additionally gated
                on isAdmin. Non-admins shouldn't see the entry point
                to an admin-only page; the AdminPage's own access gate
                still catches the URL-typing case as a backstop. */}
            {user && (
              <nav className="hidden md:flex items-center space-x-1">
                {isAdmin === true && (
                  <button
                    onClick={() => navigateToTab('dashboard')}
                    className="px-4 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <BarChart3 className="w-4 h-4" />
                      <span>Dashboard</span>
                    </div>
                  </button>
                )}
                <button
                  onClick={() => navigateToTab('community')}
                  className="px-4 py-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4" />
                    <span>Community</span>
                  </div>
                </button>
              </nav>
            )}

            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink">
              {user && <div className="hidden sm:block"><ActiveStrategyBadge /></div>}
              {user ? (
                <>
                  <Suspense fallback={null}>
                    <FeedbackWidget variant="header" />
                  </Suspense>
                  <span className="text-sm text-slate-300 hidden lg:inline">{user.email}</span>
                  <button
                    onClick={() => signOut()}
                    className="flex items-center px-3 sm:px-4 py-2 bg-white/10 hover:bg-white/15 rounded-lg font-medium transition-colors whitespace-nowrap"
                  >
                    <LogOut className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Sign Out</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleSignIn}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 rounded-lg font-medium shadow-lg shadow-blue-500/20 transition-all duration-200"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={handleSignUp}
                    className="px-4 py-2 bg-white/10 hover:bg-white/15 rounded-lg font-medium transition-colors"
                  >
                    Sign Up
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode={authMode}
      />
    </>
  );
};

export default Header;